import React, { useContext, useEffect, useState } from 'react'
import { apiGet } from '../../utils/apiFetch'
import apiPath from '../../utils/apiPath'
import Table from './Table'
import AddBackgroundCheckPricing from './AddBackgroundCheckPricing'
import EditBackgroundCheckPricing from './EditBackgroundCheckPricing'
import Pagination from '../Pagination'
import { useTranslation } from 'react-i18next'
import AuthContext from 'context/AuthContext'
import helpers from '../../utils/helpers'
import { FiSearch } from 'react-icons/fi'

function BackgroundCheckPricing () {
  const { t } = useTranslation()
  const [showModal, setShowModal] = useState(false)
  const [paginationObj, setPaginationObj] = useState({
    page: 1,
    pageCount: 1,
    pageRangeDisplayed: 10
  })
  const { logoutUser, user } = useContext(AuthContext)
  const manager = user?.permission?.find(e => e.manager === 'background_check_pricing') ?? {}
  const [editShowModal, setEditShowModal] = useState(false)
  const [pricingList, setPricingList] = useState([])
  const [page, setPage] = useState(1)
  const [pageSize] = useState(10)
  const [item, setItem] = useState('')

  // 🔍 NEW: search + filter state
  const [keyword, setKeyword] = useState('')
  const [statusFilter, setStatusFilter] = useState('')

  const [sort, setSort] = useState({
    sortBy: 'createdAt',
    sortType: 'desc'
  })

  const handleApiError = error => {
    console.error('error in get all background check pricing list==>>>>', error.message)
    if (error.response?.status === 401 || error.response?.status === 409) {
      logoutUser()
    }
  }

  const getAllBackgroundCheckPricing = async payload => {
    try {
      if (payload?.deletePage) {
        payload = { page, pageSize }
        if (pricingList?.length >= 1) {
          setPage(page - 1)
          setPaginationObj({ ...paginationObj, page: page - 1 })
        }
      } else {
        payload = {
          page,
          pageSize,
          sortBy: sort.sortBy,
          sortType: sort.sortType,
          keyword: keyword || undefined,
          status: statusFilter || undefined
        }
      }

      const path = apiPath.getBackgroundCheckPricing
      const result = await apiGet(path, payload)
      if (result?.status === 200) {
        const response = result?.data?.results
        const resultStatus = result?.data?.success
        setPricingList(response?.docs)
        setPaginationObj({
          ...paginationObj,
          page: helpers?.ternaryCondition(resultStatus, response.page, null),
          pageCount: helpers?.ternaryCondition(resultStatus, response.totalPages, null),
          perPageItem: helpers?.ternaryCondition(resultStatus, response?.docs.length, null),
          totalItems: helpers?.ternaryCondition(resultStatus, response.totalDocs, null)
        })
      }
    } catch (error) {
      handleApiError(error)
    }
  }

  // 🔁 Refetch whenever page, sort, keyword or status changes
  useEffect(() => {
    getAllBackgroundCheckPricing()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sort, page, keyword, statusFilter])

  // 🔁 Debounced search — resets to page 1 when user types
  useEffect(() => {
    const timer = setTimeout(() => {
      setPage(1)
      setPaginationObj(prev => ({ ...prev, page: 1 }))
    }, 400)
    return () => clearTimeout(timer)
  }, [keyword, statusFilter])

  const handelEdit = items => {
    setItem(items)
    setEditShowModal(true)
  }

  const handlePageClick = event => {
    const newPage = event.selected + 1
    setPage(newPage)
  }

  const clearFilters = () => {
    setKeyword('')
    setStatusFilter('')
  }

  return (
    <div>
      <div className='bg-[#F9F9F9]'>
        <div className='px-3 py-4'>
          <div className='bg-white border border-[#E9EDF9] rounded-lg'>
            {/* ──────────── Search + Filter + Add button ──────────── */}
            <div className='border-b-[#E3E3E3] border-b grid 2xl:grid-cols-3 xl:grid-cols-2 lg:grid lg:grid-cols-1 gap-3 px-4 py-3'>

              {/* Search input */}
              <div className='relative col-span-1'>
                <FiSearch className='absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4' />
                <input
                  type='text'
                  value={keyword}
                  onChange={e => setKeyword(e.target.value)}
                  placeholder={t('SEARCH_BY_CHECK_NAME') || 'Search by check name...'}
                  className='w-full pl-9 pr-3 py-2 text-sm border border-[#DFDFDF] rounded-lg focus:outline-none focus:ring-0 focus:border-LightBlue'
                />
              </div>

              {/* Status filter */}
              <div className='col-span-1'>
                <select
                  value={statusFilter}
                  onChange={e => setStatusFilter(e.target.value)}
                  className='w-full px-3 py-2 text-sm border border-[#DFDFDF] rounded-lg focus:outline-none focus:ring-0 focus:border-LightBlue bg-white'
                >
                  <option key='' value=''>{t('ALL_STATUS') || 'All Status'}</option>
                  <option key='active' value='active'>{t('ACTIVE') || 'Active'}</option>
                  <option key='inactive' value='inactive'>{t('INACTIVE') || 'Inactive'}</option>
                </select>
              </div>

              {/* Clear + Add button */}
              <div className='flex items-center gap-2 2xl:justify-end'>
                {(keyword || statusFilter) && (
                  <button
                    type='button'
                    onClick={clearFilters}
                    className='text-sm text-gray-600 hover:text-LightBlue underline'
                  >
                    {t('CLEAR') || 'Clear'}
                  </button>
                )}

                {(manager?.add || user?.role === 'admin') && (
                  <button
                    title={t('ADD_BACKGROUND_CHECK_PRICING')}
                    className='bg-LightBlue flex text-sm px-8 py-2 rounded-lg items-center border border-transparent text-white hover:bg-DarkBlue whitespace-nowrap ml-auto'
                    onClick={() => setShowModal(true)}
                  >
                    + {t('ADD_BACKGROUND_CHECK_PRICING')}
                  </button>
                )}
              </div>
            </div>

            <Table
              pricingList={pricingList}
              getAllBackgroundCheckPricing={getAllBackgroundCheckPricing}
              handelEdit={handelEdit}
              page={page}
              setSort={setSort}
              sort={sort}
              manager={manager}
              paginationObj={paginationObj}
            />

            {paginationObj?.totalItems !== 0 && (
              <Pagination
                handlePageClick={handlePageClick}
                options={paginationObj}
                page={page}
              />
            )}
          </div>
        </div>
      </div>

      <>
        {showModal && (
          <AddBackgroundCheckPricing
            setShowModal={setShowModal}
            getAllBackgroundCheckPricing={getAllBackgroundCheckPricing}
          />
        )}
        {editShowModal && (
          <EditBackgroundCheckPricing
            setEditShowModal={setEditShowModal}
            getAllBackgroundCheckPricing={getAllBackgroundCheckPricing}
            item={item}
          />
        )}
      </>
    </div>
  )
}

export default BackgroundCheckPricing